import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Search,
  Download,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Banknote,
  Building2,
  RefreshCw,
  AlertCircle,
  Copy,
  Check,
  X,
  QrCode,
  Smartphone,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import { formatCurrency, formatDate, formatRelativeTime, getInitials, exportToCSV } from '@/lib/utils';
import toast from 'react-hot-toast';
import { transactionsApi } from '@/lib/api';

interface Withdrawal {
  _id: string;
  userId: {
    _id: string;
    name: string;
    phone: string;
    email?: string;
  };
  amount: number;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  paymentMethod: string;
  upiId?: string;
  bankDetails?: {
    accountName: string;
    accountNumber: string;
    ifscCode: string;
    bankName: string;
  };
  description?: string;
  createdAt: string;
  completedAt?: string;
  failureReason?: string;
  adminNotes?: string;
}

export default function Withdrawals() {
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);

  // Modal states
  const [selectedWithdrawal, setSelectedWithdrawal] = useState<Withdrawal | null>(null);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const itemsPerPage = 15;

  const fetchWithdrawals = useCallback(async () => {
    setLoading(true);
    try {
      const response = await transactionsApi.getPendingWithdrawals();
      const { withdrawals: fetchedWithdrawals } = response.data.data;
      setWithdrawals(fetchedWithdrawals || []);
    } catch (error: any) {
      console.error('Failed to fetch withdrawals:', error);
      toast.error('Failed to load withdrawals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWithdrawals();
  }, [fetchWithdrawals]);

  // Filter withdrawals
  const filteredWithdrawals = withdrawals.filter((w) => {
    if (!search && statusFilter === 'all') return true;

    const matchesSearch =
      w.userId?.name?.toLowerCase().includes(search.toLowerCase()) ||
      w._id.includes(search) ||
      w.userId?.phone?.includes(search) ||
      w.upiId?.toLowerCase().includes(search.toLowerCase());

    const matchesStatus = statusFilter === 'all' || w.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredWithdrawals.length / itemsPerPage);
  const paginatedWithdrawals = filteredWithdrawals.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Stats
  const pendingCount = withdrawals.filter((w) => w.status === 'pending').length;
  const pendingAmount = withdrawals
    .filter((w) => w.status === 'pending')
    .reduce((sum, w) => sum + w.amount, 0);
  const completedCount = withdrawals.filter((w) => w.status === 'completed').length;

  const handleApprove = async () => {
    if (!selectedWithdrawal) return;

    setIsProcessing(true);
    try {
      await transactionsApi.approveWithdrawal(selectedWithdrawal._id);
      toast.success('Withdrawal approved successfully');
      setShowApproveDialog(false);
      setSelectedWithdrawal(null);
      fetchWithdrawals();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to approve withdrawal');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedWithdrawal) return;

    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    setIsProcessing(true);
    try {
      await transactionsApi.rejectWithdrawal(selectedWithdrawal._id, rejectReason.trim());
      toast.success('Withdrawal rejected and amount refunded');
      setShowRejectDialog(false);
      setSelectedWithdrawal(null);
      setRejectReason('');
      fetchWithdrawals();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to reject withdrawal');
    } finally {
      setIsProcessing(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const handleExport = () => {
    exportToCSV(
      filteredWithdrawals.map((w) => ({
        ID: w._id,
        User: w.userId?.name || 'N/A',
        Phone: w.userId?.phone || 'N/A',
        Amount: w.amount,
        Status: w.status,
        'UPI ID': w.upiId || 'N/A',
        Bank: w.bankDetails?.bankName || 'N/A',
        'Created At': w.createdAt,
      })),
      'withdrawals'
    );
    toast.success('Withdrawals exported successfully');
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'pending':
      case 'processing':
        return 'warning';
      case 'failed':
      case 'cancelled':
        return 'danger';
      default:
        return 'default';
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold">Withdrawal Requests</h1>
          <p className="text-slate-400 mt-1">Manage and process user withdrawals</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={fetchWithdrawals} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card variant="glass" className="p-4 relative">
          {pendingCount > 0 && (
            <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
          )}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-400">{pendingCount}</p>
              <p className="text-sm text-slate-400">Pending Requests</p>
            </div>
          </div>
        </Card>

        <Card variant="glass" className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center">
              <Banknote className="w-5 h-5 text-violet-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-violet-400">{formatCurrency(pendingAmount)}</p>
              <p className="text-sm text-slate-400">Pending Amount</p>
            </div>
          </div>
        </Card>

        <Card variant="glass" className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-400">{completedCount}</p>
              <p className="text-sm text-slate-400">Completed Today</p>
            </div>
          </div>
        </Card>

        <Card variant="glass" className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-400">{withdrawals.length}</p>
              <p className="text-sm text-slate-400">Total Requests</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card variant="glass" className="p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search by user, phone, UPI ID, or withdrawal ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              icon={<Search className="w-4 h-4" />}
            />
          </div>
          <div className="flex gap-3">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-2 rounded-lg bg-slate-900/50 border border-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed/Rejected</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Withdrawals Table */}
      <Card variant="glass" className="overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500"></div>
          </div>
        ) : filteredWithdrawals.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No withdrawal requests found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-800">
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                      User
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                      Amount
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                      UPI ID / Bank
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                      Requested
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider text-slate-400">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/50">
                  {paginatedWithdrawals.map((withdrawal) => (
                    <tr key={withdrawal._id} className="hover:bg-slate-800/30 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white font-semibold text-sm">
                            {getInitials(withdrawal.userId?.name || 'U')}
                          </div>
                          <div>
                            <p className="font-medium">{withdrawal.userId?.name || 'Unknown'}</p>
                            <p className="text-sm text-slate-500">{withdrawal.userId?.phone || 'N/A'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-semibold text-lg">{formatCurrency(withdrawal.amount)}</span>
                      </td>
                      <td className="px-6 py-4">
                        {withdrawal.upiId ? (
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-violet-400">{withdrawal.upiId}</span>
                            <button
                              onClick={() => copyToClipboard(withdrawal.upiId!)}
                              className="p-1 hover:bg-slate-700 rounded"
                            >
                              <Copy className="w-3 h-3 text-slate-400" />
                            </button>
                          </div>
                        ) : withdrawal.bankDetails ? (
                          <div>
                            <p className="font-medium text-sm">{withdrawal.bankDetails.bankName}</p>
                            <p className="text-xs text-slate-500">
                              A/C: {withdrawal.bankDetails.accountNumber}
                            </p>
                          </div>
                        ) : (
                          <span className="text-slate-500">N/A</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <Badge variant={getStatusBadgeVariant(withdrawal.status) as any}>
                          {withdrawal.status}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm">{formatDate(withdrawal.createdAt)}</p>
                          <p className="text-xs text-slate-500">{formatRelativeTime(withdrawal.createdAt)}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedWithdrawal(withdrawal)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {withdrawal.status === 'pending' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-emerald-400 hover:text-emerald-300"
                                onClick={() => {
                                  setSelectedWithdrawal(withdrawal);
                                  setShowApproveDialog(true);
                                }}
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-400 hover:text-red-300"
                                onClick={() => {
                                  setSelectedWithdrawal(withdrawal);
                                  setShowRejectDialog(true);
                                }}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-800">
              <p className="text-sm text-slate-400">
                Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                {Math.min(currentPage * itemsPerPage, filteredWithdrawals.length)} of{' '}
                {filteredWithdrawals.length}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm">
                  Page {currentPage} of {totalPages || 1}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages || totalPages === 0}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>

      {/* Detail Modal */}
      <Dialog open={!!selectedWithdrawal && !showApproveDialog && !showRejectDialog} onOpenChange={() => setSelectedWithdrawal(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Withdrawal Details</DialogTitle>
            <DialogDescription>Request ID: {selectedWithdrawal?._id}</DialogDescription>
          </DialogHeader>
          {selectedWithdrawal && (
            <div className="py-4 space-y-4">
              {/* User Info */}
              <div className="flex items-center gap-4 pb-4 border-b border-slate-800">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white font-bold">
                  {getInitials(selectedWithdrawal.userId?.name || 'U')}
                </div>
                <div>
                  <p className="font-semibold text-lg">{selectedWithdrawal.userId?.name || 'Unknown'}</p>
                  <p className="text-sm text-slate-400">{selectedWithdrawal.userId?.phone || 'N/A'}</p>
                </div>
              </div>

              {/* Amount & Status */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-400">Amount</p>
                  <p className="text-2xl font-bold text-violet-400">
                    {formatCurrency(selectedWithdrawal.amount)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Status</p>
                  <Badge variant={getStatusBadgeVariant(selectedWithdrawal.status) as any} className="mt-1">
                    {selectedWithdrawal.status}
                  </Badge>
                </div>
              </div>

              {/* UPI ID with QR Code (prominently displayed) */}
              {selectedWithdrawal.upiId && (
                <div className="p-4 bg-violet-500/10 border border-violet-500/20 rounded-lg">
                  <div className="flex items-start gap-6">
                    {/* QR Code Section */}
                    <div className="flex flex-col items-center">
                      <div className="p-3 bg-white rounded-xl shadow-lg mb-2">
                        <QRCodeSVG
                          value={`upi://pay?pa=${encodeURIComponent(selectedWithdrawal.upiId)}&pn=${encodeURIComponent(selectedWithdrawal.userId?.name || 'User')}&am=${selectedWithdrawal.amount}&cu=INR`}
                          size={140}
                          bgColor="#ffffff"
                          fgColor="#000000"
                          level="M"
                          includeMargin={false}
                        />
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-emerald-400">
                        <Smartphone className="w-3.5 h-3.5" />
                        <span>Scan to Pay</span>
                      </div>
                    </div>

                    {/* UPI Details Section */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-3">
                        <QrCode className="w-5 h-5 text-violet-400" />
                        <h4 className="font-semibold text-violet-400">UPI Payment</h4>
                      </div>

                      <div className="space-y-3">
                        <div>
                          <p className="text-xs text-slate-400 mb-1">UPI ID</p>
                          <div className="flex items-center gap-2">
                            <p className="text-lg font-bold text-white font-mono">{selectedWithdrawal.upiId}</p>
                            <button
                              onClick={() => copyToClipboard(selectedWithdrawal.upiId!)}
                              className="p-1.5 hover:bg-slate-700 rounded-md transition-colors"
                              title="Copy UPI ID"
                            >
                              <Copy className="w-4 h-4 text-slate-400" />
                            </button>
                          </div>
                        </div>

                        <div className="flex gap-4">
                          <div>
                            <p className="text-xs text-slate-400 mb-1">Amount</p>
                            <p className="text-lg font-bold text-emerald-400">₹{selectedWithdrawal.amount}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-400 mb-1">Recipient</p>
                            <p className="font-medium text-white">{selectedWithdrawal.userId?.name || 'User'}</p>
                          </div>
                        </div>
                      </div>

                      <div className="mt-3 p-2 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                        <p className="text-xs text-amber-400 flex items-center gap-1.5">
                          <AlertCircle className="w-3.5 h-3.5" />
                          Scan QR with any UPI app to pay directly
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Bank Details */}
              {selectedWithdrawal.bankDetails && (
                <div className="p-4 bg-slate-800/30 rounded-lg space-y-3">
                  <h4 className="font-medium">Bank Details</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-slate-400">Account Name</p>
                      <p>{selectedWithdrawal.bankDetails.accountName}</p>
                    </div>
                    <div>
                      <p className="text-slate-400">Account Number</p>
                      <p className="font-mono">{selectedWithdrawal.bankDetails.accountNumber}</p>
                    </div>
                    <div>
                      <p className="text-slate-400">Bank</p>
                      <p>{selectedWithdrawal.bankDetails.bankName}</p>
                    </div>
                    <div>
                      <p className="text-slate-400">IFSC Code</p>
                      <p className="font-mono">{selectedWithdrawal.bankDetails.ifscCode}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-400">Requested At</p>
                  <p>{formatDate(selectedWithdrawal.createdAt)}</p>
                </div>
                {selectedWithdrawal.completedAt && (
                  <div>
                    <p className="text-slate-400">Completed At</p>
                    <p>{formatDate(selectedWithdrawal.completedAt)}</p>
                  </div>
                )}
              </div>

              {/* Failure Reason */}
              {selectedWithdrawal.failureReason && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <p className="text-sm text-red-400">
                    <strong>Rejection Reason:</strong> {selectedWithdrawal.failureReason}
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              {selectedWithdrawal.status === 'pending' && (
                <div className="flex gap-3 pt-4 border-t border-slate-800">
                  <Button
                    className="flex-1 bg-emerald-600 hover:bg-emerald-500"
                    onClick={() => {
                      setShowApproveDialog(true);
                    }}
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Approve & Process
                  </Button>
                  <Button
                    variant="destructive"
                    className="flex-1"
                    onClick={() => {
                      setShowRejectDialog(true);
                    }}
                  >
                    <X className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-400">
              <CheckCircle className="w-5 h-5" />
              Approve Withdrawal
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to approve this withdrawal request?
            </DialogDescription>
          </DialogHeader>
          {selectedWithdrawal && (
            <div className="py-4">
              <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg space-y-2">
                <p className="text-emerald-400 font-semibold text-lg">
                  {formatCurrency(selectedWithdrawal.amount)} to {selectedWithdrawal.userId?.name}
                </p>
                {selectedWithdrawal.upiId && (
                  <p className="text-sm text-slate-300">
                    UPI: <span className="font-mono text-violet-400">{selectedWithdrawal.upiId}</span>
                  </p>
                )}
                {selectedWithdrawal.bankDetails && (
                  <p className="text-sm text-slate-400">
                    Bank: {selectedWithdrawal.bankDetails.bankName}
                  </p>
                )}
              </div>
            </div>
          )}
          <DialogFooter className="gap-3">
            <Button variant="outline" onClick={() => setShowApproveDialog(false)}>
              Cancel
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-500"
              onClick={handleApprove}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Approve & Process
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <XCircle className="w-5 h-5" />
              Reject Withdrawal
            </DialogTitle>
            <DialogDescription>
              The amount will be refunded to the user's wallet.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Rejection Reason *
            </label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-slate-900/50 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500/50 resize-none"
              rows={3}
              placeholder="Enter rejection reason..."
            />
          </div>
          <DialogFooter className="gap-3">
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={isProcessing || !rejectReason.trim()}
            >
              {isProcessing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <X className="w-4 h-4 mr-2" />
                  Reject & Refund
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
